import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import { FRAIS, OfflineError } from '../api/client.js';
import {
  type Task,
  type View,
  type ListTasksOpts,
  listTasks,
  createTask,
  updateTask,
  rescheduleTask,
  completeTask,
  deleteTask,
} from '../api/tasks.js';
import { newTempId, type CreateTaskInput } from '../lib/offline/queue.js';
import { useOffline, hasPending } from './offline.js';
import { truncate } from '../lib/string.js';
import { texteBrut } from '../lib/linkify.js';
import { useToast } from './toast.js';

interface CurrentView {
  view: View;
  opts: ListTasksOpts;
}

interface TasksState {
  tasks: Task[];
  currentView: CurrentView | null;
  loading: boolean;
  error: string | null;
  load: (view: View, opts?: ListTasksOpts) => Promise<void>;
  reloadCurrent: () => Promise<void>;
  refresh: (view: View, opts?: ListTasksOpts) => Promise<void>;
  create: (input: Parameters<typeof createTask>[0]) => Promise<Task>;
  update: (tid: string, patch: Partial<Task>) => Promise<void>;
  reschedule: (tid: string, dueDate: string | null) => Promise<void>;
  complete: (tid: string) => Promise<void>;
  remove: (tid: string) => Promise<void>;
}

/**
 * Une tâche fabriquée localement, le temps que la file parte. Elle porte un id
 * `tmp_` que le drain remplacera par celui du serveur
 * ([ADR 0010](../../../docs/adr/0010-ecritures-offline-file-de-rejeu.md)).
 */
function optimisticTask(tempId: string, input: CreateTaskInput): Task {
  return {
    taskId: tempId,
    title: input.title,
    description: input.description ?? null,
    projectId: input.projectId,
    dueDate: input.dueDate ?? null,
    rrule: input.rrule ?? null,
    subtasks: (input.subtasks ?? []).map((s, i) => ({
      id: `${tempId}-s${i}`,
      title: s.title,
      done: false,
      order: i,
    })),
    createdAt: new Date().toISOString(),
  };
}

export const useTasks = create<TasksState>((set, get) => ({
  tasks: [],
  currentView: null,
  loading: false,
  error: null,
  load: async (view, opts = {}) => {
    set({ loading: true, error: null, currentView: { view, opts } });
    try {
      set({ tasks: await listTasks(view, opts), loading: false });
    } catch (err) {
      set({ error: errorMessage(err), loading: false });
    }
  },

  /**
   * La re-lecture est **sautée tant que la file n'est pas vide**. Hors-ligne, ce GET
   * est servi par le cache du service worker : il annulerait visuellement l'action
   * qu'on vient de mettre en file — le bug du 2026-04-24 sous une autre forme
   * ([LEARNINGS.md](../../../LEARNINGS.md)). L'état local fait alors foi.
   */
  reloadCurrent: async () => {
    const c = get().currentView;
    if (!c || hasPending()) return;
    try {
      set({ tasks: await listTasks(c.view, c.opts) });
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
    }
  },

  /**
   * Tirer pour rafraîchir : la liste est relue **au serveur** (`FRAIS`), sans repasser par le
   * squelette — elle reste affichée pendant l'appel. Une erreur remonte à l'appelant, qui la dit.
   * Même règle que `reloadCurrent` : une file non vide fait foi, on ne relit pas par-dessus —
   * vérifiée encore au retour, comme la vue : une écriture mise en file ou une autre page
   * ouverte pendant l'appel l'emportent sur la réponse.
   */
  refresh: async (view, opts = {}) => {
    if (hasPending()) return;
    const tasks = await listTasks(view, opts, FRAIS);
    if (hasPending() || get().currentView?.view !== view) return;
    set({ tasks, error: null });
  },

  create: async (input) => {
    try {
      const task = await createTask(input);
      set({ tasks: [...get().tasks, task] });
      await get().reloadCurrent();
      return task;
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      const tempId = newTempId();
      const task = optimisticTask(tempId, input);
      useOffline.getState().enqueue({ kind: 'task.create', id: tempId, input });
      set({ tasks: [...get().tasks, task] });
      return task;
    }
  },

  update: async (tid, patch) => {
    const apply = (): void =>
      set({ tasks: get().tasks.map((t) => (t.taskId === tid ? { ...t, ...patch } : t)) });
    try {
      await updateTask(tid, patch);
      apply();
      // Le PATCH est passé et l'état local l'a appliqué : une re-lecture ratée ne doit pas
      // remonter comme un échec d'enregistrement (TaskDetailModal l'afficherait).
      await get().reloadCurrent().catch(() => undefined);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline.getState().enqueue({ kind: 'task.update', id: tid, patch });
      apply();
    }
  },

  reschedule: async (tid, dueDate) => {
    const apply = (): void =>
      set({ tasks: get().tasks.map((t) => (t.taskId === tid ? { ...t, dueDate } : t)) });
    try {
      await rescheduleTask(tid, dueDate);
      apply();
      await get().reloadCurrent();
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline.getState().enqueue({ kind: 'task.reschedule', id: tid, dueDate });
      apply();
    }
  },

  complete: async (tid) => {
    const snapshot = get().tasks.find((t) => t.taskId === tid);
    let queued = false;
    try {
      await completeTask(tid);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline.getState().enqueue({ kind: 'task.complete', id: tid });
      queued = true;
    }

    // Une récurrente revient à sa prochaine échéance, une simple disparaît. Hors-ligne
    // on ne sait pas calculer la suivante sans le serveur : la retirer de la vue est le
    // choix le moins surprenant, le rejeu rétablira la bonne date.
    set({ tasks: get().tasks.filter((t) => t.taskId !== tid) });
    if (!queued) await get().reloadCurrent();

    if (snapshot) {
      useToast.getState().show({
        message: `Completed · ${truncate(texteBrut(snapshot.title), 40)}`,
        action: {
          label: 'Undo',
          run: async () => {
            if (snapshot.rrule) {
              // La tâche a été retirée de la liste au `complete`. En ligne, le
              // `reloadCurrent()` la ramenait ; hors-ligne il est suspendu, donc sans
              // cette remise en place l'Undo n'a aucun effet visible et paraît cassé.
              if (!get().tasks.some((t) => t.taskId === tid)) {
                set({ tasks: [...get().tasks, snapshot] });
              }
              await get().reschedule(tid, snapshot.dueDate);
            } else {
              await get().create({
                title: snapshot.title,
                projectId: snapshot.projectId,
                description: snapshot.description,
                dueDate: snapshot.dueDate,
                rrule: snapshot.rrule,
                subtasks: snapshot.subtasks.map((s) => ({ title: s.title })),
              });
            }
            await get().reloadCurrent();
          },
        },
      });
    }
  },

  remove: async (tid) => {
    try {
      await deleteTask(tid);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline.getState().enqueue({ kind: 'task.delete', id: tid });
    }
    set({ tasks: get().tasks.filter((t) => t.taskId !== tid) });
  },
}));
